'use client'

import React, { Fragment, useMemo, useState } from 'react'
import { ChevronRight } from "@excellent/ui/icons"
import { useApi } from '@excellent/ui/api'
import { lens } from '@excellent/ui/api/contracts/lens'
import {
  Card,
  CardContent,
} from "@excellent/ui"
import { PageHeader, SearchInput } from "@excellent/ui"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@excellent/ui"
import { Skeleton } from "@excellent/ui"
import { useSortableData } from "@excellent/ui"
import { useSnapshot } from '@lens/lib/use-snapshot'
import { formatDate } from '@lens/lib/decode'

interface PlanFile {
  name: string
  content: string
  mtime: string
}

function wordCount(text: string) {
  return text.trim().split(/\s+/).filter(Boolean).length
}

function lineCount(text: string) {
  return text.split('\n').filter(l => l.trim()).length
}

function extractTitle(content: string): string | null {
  const firstLine = content.split('\n').find(l => l.trim())
  if (firstLine?.startsWith('#')) return firstLine.replace(/^#+\s*/, '').trim()
  return null
}

function MarkdownLine({ line }: { line: string }) {
  if (/^#\s/.test(line))
    return (
      <p className="text-foreground type-body mt-4 mb-1 first:mt-0">
        {line.replace(/^#\s/, '')}
      </p>
    )
  if (/^##\s/.test(line))
    return (
      <p className="text-foreground type-body mt-3 mb-1">
        {line.replace(/^##\s/, '')}
      </p>
    )
  if (/^###\s/.test(line))
    return (
      <p className="text-foreground type-body mt-2 mb-0.5">
        {line.replace(/^###\s/, '')}
      </p>
    )
  if (/^---+$/.test(line.trim())) return <hr className="border-border my-3" />
  if (!line.trim()) return <div className="h-2" />
  if (/^[-*]\s/.test(line))
    return (
      <p className="text-foreground type-body leading-body flex gap-2">
        <span className="text-foreground flex-shrink-0">›</span>
        <InlineMarkdown text={line.replace(/^[-*]\s/, '')} />
      </p>
    )
  if (/^\d+\.\s/.test(line)) {
    const [num, ...rest] = line.split(/\.\s(.+)/)
    return (
      <p className="text-foreground type-body leading-body flex gap-2">
        <span className="text-foreground flex-shrink-0 w-5 text-right">{num}.</span>
        <InlineMarkdown text={rest.join('. ')} />
      </p>
    )
  }
  if (line.startsWith('```'))
    return <span className="text-foreground type-body">{line}</span>

  return (
    <p className="text-foreground type-body leading-body">
      <InlineMarkdown text={line} />
    </p>
  )
}

function InlineMarkdown({ text }: { text: string }) {
  const parts = text.split(/(`[^`]+`|\*\*\*[^*]+\*\*\*|\*\*[^*]+\*\*|\*[^*]+\*)/)
  return (
    <>
      {parts.map((p, i) => {
        if (p.startsWith('***') && p.endsWith('***'))
          return <span key={i} className="text-foreground italic">{p.slice(3, -3)}</span>
        if (p.startsWith('**') && p.endsWith('**'))
          return <span key={i} className="text-foreground">{p.slice(2, -2)}</span>
        if (p.startsWith('*') && p.endsWith('*'))
          return <em key={i} className="text-foreground italic">{p.slice(1, -1)}</em>
        if (p.startsWith('`') && p.endsWith('`'))
          return (
            <code
              key={i}
              className="text-foreground bg-muted px-1 py-0.5 rounded type-body"
            >
              {p.slice(1, -1)}
            </code>
          )
        return <span key={i}>{p}</span>
      })}
    </>
  )
}

function MarkdownContent({ content }: { content: string }) {
  const lines = content.split('\n')
  const result: React.ReactNode[] = []
  let inCodeBlock = false
  let codeLines: string[] = []
  let codeLang = ''

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!
    if (line.startsWith('```')) {
      if (!inCodeBlock) {
        inCodeBlock = true
        codeLang = line.slice(3).trim()
        codeLines = []
      } else {
        inCodeBlock = false
        result.push(
          <div
            key={i}
            className="my-2 rounded-lg surface-edge bg-muted/80 dark:bg-muted overflow-x-auto"
          >
            {codeLang && (
              <div className="px-3 py-1.5 border-b border-border type-body text-foreground tracking-wide">
                {codeLang}
              </div>
            )}
            <pre className="px-3 py-2.5 type-body text-foreground leading-body">
              {codeLines.join('\n')}
            </pre>
          </div>
        )
        codeLines = []
        codeLang = ''
      }
      continue
    }
    if (inCodeBlock) {
      codeLines.push(line)
      continue
    }
    result.push(<MarkdownLine key={i} line={line} />)
  }

  return <div className="space-y-0.5">{result}</div>
}

type SortKey = 'title' | 'mtime' | 'lines' | 'words'

function SortHeader({
  label,
  k,
  isActive,
  arrow,
  onSort,
  align = 'left',
}: {
  label: string
  k: SortKey
  isActive: boolean
  arrow: '↓' | '↑' | ''
  onSort: (k: SortKey) => void
  align?: 'left' | 'right'
}) {
  return (
    <button
      type="button"
      onClick={() => onSort(k)}
      className={`${align === 'right' ? 'text-right' : 'text-left'} whitespace-nowrap text-muted-foreground hover:opacity-60 transition-opacity w-full`}
    >
      {label} {isActive ? arrow : ''}
    </button>
  )
}

export default function PlansPage() {
  const { data, error, isLoading } = useApi(lens.plans, {}) as { data: { plans: PlanFile[] } | undefined; error: unknown; isLoading: boolean }
  const { snapshot } = useSnapshot()
  const scopedProject = snapshot?.scopedProject ?? null
  const [search, setSearch] = useState('')
  const [expanded, setExpanded] = useState<string | null>(null)

  const plans = data?.plans ?? []

  const filtered = useMemo(() => {
    if (!search) return plans
    const q = search.toLowerCase()
    return plans.filter(
      p =>
        p.name.toLowerCase().includes(q) ||
        p.content.toLowerCase().includes(q)
    )
  }, [plans, search])

  const comparators = useMemo(() => ({
    title: (a: PlanFile, b: PlanFile) =>
      (extractTitle(a.content) ?? a.name).toLowerCase().localeCompare(
        (extractTitle(b.content) ?? b.name).toLowerCase(),
      ),
    mtime: (a: PlanFile, b: PlanFile) =>
      new Date(a.mtime).getTime() - new Date(b.mtime).getTime(),
    lines: (a: PlanFile, b: PlanFile) => lineCount(a.content) - lineCount(b.content),
    words: (a: PlanFile, b: PlanFile) => wordCount(a.content) - wordCount(b.content),
  }), [])

  const { sorted, isActive, arrow, toggle } = useSortableData<PlanFile, SortKey>(
    filtered,
    comparators,
    { key: 'mtime', dir: 'desc' },
  )

  const toggleSort = (key: SortKey) => toggle(key, key !== 'title')

  const subtitle = data
    ? filtered.length !== plans.length
      ? `${filtered.length.toLocaleString()} of ${plans.length.toLocaleString()} plans`
      : `${plans.length.toLocaleString()} plans`
    : undefined

  return (
    <div className="flex flex-col">
      <div className="space-y-3">

        <PageHeader
          title="Plans"
          description={subtitle}
          toolbar={
            data ? (
              <SearchInput
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by name or content…"
              />
            ) : undefined
          }
        />

        {scopedProject && (
          <p className="type-body text-muted-foreground">
            Showing plans referenced in <span className="text-foreground">{scopedProject.name}</span> sessions. Plans are stored globally in <code className="text-foreground">~/.claude/plans/</code>; switch to <span className="text-foreground">All projects</span> to see every plan.
          </p>
        )}

        {error ? (
          <p className="text-destructive type-body">Error: {String(error)}</p>
        ) : null}

        {isLoading && (
          <div className="space-y-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-10 rounded" />
            ))}
          </div>
        )}

        {data && (
          <Card>
            <CardContent>
              <Table className="[&_tr>*:first-child]:pl-0 [&_tr>*:last-child]:pr-0">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8"></TableHead>
                    <TableHead>
                      <SortHeader label="Title" k="title" isActive={isActive('title')} arrow={arrow('title')} onSort={toggleSort} />
                    </TableHead>
                    <TableHead className="w-32">
                      <SortHeader label="Date" k="mtime" isActive={isActive('mtime')} arrow={arrow('mtime')} onSort={toggleSort} />
                    </TableHead>
                    <TableHead className="w-24">
                      <SortHeader label="Lines" k="lines" isActive={isActive('lines')} arrow={arrow('lines')} onSort={toggleSort} />
                    </TableHead>
                    <TableHead className="w-24">
                      <SortHeader label="Words" k="words" isActive={isActive('words')} arrow={arrow('words')} onSort={toggleSort} />
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sorted.map(plan => {
                    const title = extractTitle(plan.content) ?? plan.name
                    const isOpen = expanded === plan.name
                    return (
                      <Fragment key={plan.name}>
                        <TableRow
                          className="cursor-pointer hover:bg-muted/40"
                          onClick={() => setExpanded(isOpen ? null : plan.name)}
                        >
                          <TableCell className="w-8">
                            <ChevronRight
                              strokeWidth={1.5}
                              className={`w-4 h-4 text-foreground transition-transform ${isOpen ? 'rotate-90' : ''}`}
                              aria-hidden
                            />
                          </TableCell>
                          <TableCell className="max-w-0">
                            <div className="truncate text-foreground" title={title}>
                              {title}
                            </div>
                            {title !== plan.name && (
                              <div className="truncate type-body text-foreground" title={plan.name}>
                                {plan.name}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="tabular-nums whitespace-nowrap">
                            {formatDate(plan.mtime)}
                          </TableCell>
                          <TableCell className="tabular-nums">
                            {lineCount(plan.content).toLocaleString()}
                          </TableCell>
                          <TableCell className="tabular-nums">
                            {wordCount(plan.content).toLocaleString()}
                          </TableCell>
                        </TableRow>
                        {isOpen && (
                          <TableRow className="hover:bg-transparent">
                            <TableCell colSpan={5} className="p-0 whitespace-normal">
                              <div className="bg-muted/25 dark:bg-muted/10 px-6 py-5 max-h-[min(70vh,56rem)] overflow-y-auto">
                                <MarkdownContent content={plan.content} />
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    )
                  })}
                  {sorted.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground py-8 type-body">
                        {plans.length === 0
                          ? 'No plans found in ~/.claude/plans/'
                          : 'No plans match your search.'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}
